// feature/lawyer-historial.
// Audit-log feed for the lawyer's own recent activity. Sits behind the
// new "Historial" tab on the Mis casos page. Default window is 7 days,
// expandable to 14 / 30 / 90 via a small select. Click a row to open
// the case detail (same AbogadoCaseDetail used by the priority list).

import React, { useEffect, useState } from "react";
import { cases as casesApi } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import { TipoPill, EstadoPill, TipoFalloPill } from "../../components/Pills";
import AbogadoCaseDetail from "./AbogadoCaseDetail";

const WINDOW_OPTIONS = [
  { value: 7,  label: "Últimos 7 días" },
  { value: 14, label: "Últimos 14 días" },
  { value: 30, label: "Últimos 30 días" },
  { value: 90, label: "Últimos 90 días" },
];

// Spanish labels + tone for the action types the backend filters by
// (see RELEVANT in routes/cases.js GET /lawyer-historial). Anything
// not in this map renders with the raw action key as a fallback.
const ACTION_META = {
  "case.routed_to_lawyer":         { label: "Caso recibido",        icon: "📥", tone: "info" },
  "case.marked_contestada":        { label: "Marcó contestada",     icon: "✅", tone: "success" },
  "case.esperando_insumo":         { label: "Marcó Esperando insumo", icon: "⏸️", tone: "warning" },
  "case.moderation_requested":     { label: "Pidió moderación",     icon: "🛡️", tone: "warning" },
  "case.sancion_transition":       { label: "Transición sanción",   icon: "🔁", tone: "info" },
  "case.archivado":                { label: "Caso archivado",       icon: "📦", tone: "muted" },
  "case.admin_sent_back_to_lawyer":{ label: "Le devolvieron el caso", icon: "↩️", tone: "danger" },
  "case.reassign":                 { label: "Reasignación",         icon: "🔀", tone: "info" },
};

function toneColor(tone) {
  switch (tone) {
    case "success": return "var(--color-success)";
    case "warning": return "var(--color-warning)";
    case "danger":  return "var(--color-danger)";
    case "info":    return "var(--color-info)";
    default:        return "var(--text-muted)";
  }
}

function relativeTime(iso) {
  if (!iso) return "—";
  const ms  = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1)   return "ahora";
  if (min < 60)  return `hace ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24)   return `hace ${hr} h`;
  const d = Math.floor(hr / 24);
  if (d < 30)    return `hace ${d} d`;
  const mo = Math.floor(d / 30);
  if (mo < 12)   return `hace ${mo} mes${mo === 1 ? "" : "es"}`;
  const y = Math.floor(d / 365);
  return `hace ${y} año${y === 1 ? "" : "s"}`;
}

function absoluteTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("es-CO", {
    dateStyle: "medium", timeStyle: "short",
  });
}

export default function HistorialTab() {
  const [days, setDays]   = useState(7);
  const [data, setData]   = useState(null);
  const [openId, setOpen] = useState(null);

  function load() {
    setData(null);
    casesApi.lawyerHistorial({ days, limit: 200 })
      .then((r) => setData(r.data?.events || []))
      .catch(() => setData([]));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="col" style={{ gap: 16 }}>
      <div>
        <h1 style={{ margin: 0 }}>Historial</h1>
        <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
          Su actividad reciente: contestaciones, esperas, moderaciones, devoluciones y reasignaciones.
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label className="muted" style={{
          margin: 0, fontSize: 12, textTransform: "none", letterSpacing: 0,
        }}>
          Periodo:
        </label>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                style={{ width: "auto", padding: "4px 8px", fontSize: 12 }}>
          {WINDOW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {data != null && (
          <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
            {data.length} evento{data.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {data == null ? (
        <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
      ) : data.length === 0 ? (
        <div className="card">
          <Empty
            title="Sin actividad en este periodo"
            message="Cuando conteste un caso, marque Esperando insumo, pida moderación o cambie un estado, aparecerá aquí."
          />
        </div>
      ) : (
        <div className="col" style={{ gap: 6 }}>
          {data.map((ev) => (
            <HistorialRow key={ev.id} ev={ev} onOpen={() => setOpen(ev.case_id)} />
          ))}
        </div>
      )}

      <AbogadoCaseDetail caseId={openId} open={!!openId}
                         onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}

function HistorialRow({ ev, onOpen }) {
  const meta = ACTION_META[ev.action] || { label: ev.action, icon: "•", tone: "muted" };
  const detail = describePayload(ev);
  return (
    <div className="card clickable" onClick={ev.case_id ? onOpen : undefined}
         style={{
           padding: 12, cursor: ev.case_id ? "pointer" : "default",
           display: "flex", alignItems: "center", gap: 12,
           borderLeft: `3px solid ${toneColor(meta.tone)}`,
         }}>
      <div style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">{meta.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
          <span style={{ fontWeight: 600, color: toneColor(meta.tone) }}>{meta.label}</span>
          {ev.case_number && <strong style={{ color: "var(--text-primary)" }}>{ev.case_number}</strong>}
          {ev.tipo && <TipoPill tipo={ev.tipo} />}
          {ev.tipo === "fallo" && <TipoFalloPill value={ev.tipo_fallo} />}
          {ev.current_estado && (
            <EstadoPill c={{
              estado: ev.current_estado,
              contestada_at: ev.contestada_at,
              archivado_at:  ev.archivado_at,
            }} />
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-body)" }}>
          {ev.accionante || "—"}
          {ev.juzgado && <> · <span className="muted">{ev.juzgado}</span></>}
          {detail && <> · {detail}</>}
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>
          {relativeTime(ev.created_at)}
        </div>
        <div className="muted" style={{ fontSize: 11 }}>{absoluteTime(ev.created_at)}</div>
      </div>
    </div>
  );
}

// Compact one-line summary of the audit row's payload, when there is
// something worth surfacing inline. Keeps the row scannable; the full
// payload is still on the case detail's audit timeline.
function describePayload(ev) {
  const p = ev.payload || {};
  if (ev.action === "case.marked_contestada") {
    return ev.contestada_categoria || p.categoria || null;
  }
  if (ev.action === "case.admin_sent_back_to_lawyer" && p.reason) {
    const r = String(p.reason);
    return `“${r.length > 80 ? r.slice(0, 80) + "…" : r}”`;
  }
  if (ev.action === "case.sancion_transition" && ev.from_state && ev.to_state) {
    return `${ev.from_state} → ${ev.to_state}`;
  }
  if (ev.action === "case.moderation_requested" && p.reason) {
    const r = String(p.reason);
    return `“${r.length > 80 ? r.slice(0, 80) + "…" : r}”`;
  }
  if (ev.action === "case.archivado" && p.notes) {
    const n = String(p.notes);
    return `“${n.length > 80 ? n.slice(0, 80) + "…" : n}”`;
  }
  return null;
}
